import * as vp from 'vopay';
import { Prompt, useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import moment from 'moment';
import { useState, FunctionComponent, useEffect, useContext, JSX } from 'react';
import { Formik, FormikTouched } from 'formik';
import { SinglePaymentHandler, SinglePayment, FinancialInstitutionsType } from './types';
import styles from './SinglePaymentForm.module.scss';
import { buildSinglePaymentRows } from './buildSinglePaymentRows.helper';
import { useAccountInfo } from '@/providers/AccountInfo.provider';
import { useAccountSummary } from '@/providers/AccountSummary.provider';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import useFetchClientAccountBalance from '@/hooks/useApi/useFetchClientAccountBalance';
import useInteracAutoDeposit from '@/hooks/useApi/useInteracAutoDeposit';
import { usePendingData } from '@/providers/PendingData.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import SinglePaymentDialogueContext, { SinglePaymentCtx } from '../Dialogue/SinglePaymentContext';
import SinglePaymentDialogue from '../Dialogue/SinglePaymentDialogue';
import InteracInfoContext from '@/components/Accounts/AccountCreation/Interac/InteracInfoContext';
import SinglePaymentBankInfoContext from './SinglePaymentBankInfoContext';
import { usePermissions } from '@/providers/Permissions.provider';
import { useDataFetchIndicator } from '@/hooks';

import ElinxImage from '../Images/elinx-preview.png';
import MessageBox from '@/components/UI/MessageBox/MessageBox';
import GppGoodIcon from './svgs/gpp_good.svg';

const {
  Button,
  Box,
  Table,
  TextField,
  Select,
  Icon,
  Tooltip,
  DatepickerSimple,
  GoogleAddressField,
  MultiSelector,
  Modal,
  Toggle,
  Radio,
} = vp;

type SinglePaymentProps = {
  hasNoBankAccount: boolean;
  hasVenmoAccount: boolean;
  hasPayPalAccount: boolean;
  resetPendingData: boolean;
  isUnderReview: boolean;
  setIsUnderReview: (e: boolean) => void;
  financialInstitutions: FinancialInstitutionsType;
  financialInstitutionBranches: FinancialInstitutionsType;
  handlers: SinglePaymentHandler;
  defaultTouches: FormikTouched<SinglePayment>;
  defaultValues: SinglePayment;
  SinglePaymentContext: SinglePaymentCtx;
  setIsVisible: (e: boolean) => void;
  selectedCurrency: string;
  selectedclientAccountDetail: (ClientAccount: vp.ClientAccount) => void;
  accountHolderName?: string;
};

type StartDateType = {
  parent: {
    StartDate: string;
  };
};

const SinglePaymentForm: FunctionComponent<SinglePaymentProps> = ({
  hasNoBankAccount,
  hasVenmoAccount,
  hasPayPalAccount,
  resetPendingData,
  isUnderReview,
  setIsUnderReview,
  financialInstitutions,
  financialInstitutionBranches,
  handlers,
  defaultValues,
  defaultTouches,
  SinglePaymentContext,
  setIsVisible,
  selectedCurrency,
  selectedclientAccountDetail,
  accountHolderName,
}) => {
  const history = useHistory();
  const { setNewCurrencyInfo } = useAccountInfo();
  const { fetchAutoDepositStatus, loading } = useInteracAutoDeposit();
  const [, dataFetchIndicator] = useDataFetchIndicator();

  const [clientAccount, getClientAccount] = useState(defaultValues.ClientAccount || '');
  const { initPendingData, updatePendingData, hasPendingData, reset } = usePendingData();
  const [moreInfoNeeded, setMoreInfoNeeded] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [autoDepositEnabled, setAutoDepositEnabled] = useState(false);
  const [transactionDetailRow, contactDetailRow, addressDetailRow, bankDetailRow, interacDetailRow, requestDetailRow] =
    buildSinglePaymentRows(
      moreInfoNeeded,
      financialInstitutions,
      financialInstitutionBranches,
      defaultValues.PaymentMethod,
      defaultValues.BankingPaymentRail || '',
      selectedCurrency,
      selectedclientAccountDetail,
      autoDepositEnabled,
      accountHolderName,
    );
  const now = moment().format('YYYY-MM-DD');
  const lockedFeatures = useLockedFeatures();

  const { updateIsLocked } = useLockedFeatureDialogue();
  const [addingNewContact, setAddingNewContact] = useState(false);
  const [requestingDetails, setRequestingDetails] = useState(false);
  const { accountBalance } = useAccountSummary();
  const [addInterac, shouldAddInterac] = useState(false);
  const InteracCtx = useContext(InteracInfoContext);
  const SinglePaymentBankInfoCtx = useContext(SinglePaymentBankInfoContext);
  const [bankInfo, setBankInfo] = useState<vp.ClientBankAccountsLists>({
    ABARoutingNumber: '',
    AccountHolderName: '',
    AccountNumber: '',
    Address: '',
    BankID: '',
    BranchTransitNumber: '',
    City: '',
    ClientAccountID: '',
    ClientControlledBankAccount: '',
    ClientReferenceNumber: '',
    Currency: '',
    DateAdded: '',
    EmailAddress: '',
    FinancialInstitutionNumber: '',
    FlinksAccountID: '',
    FlinksLoginID: '',
    InstitutionName: '',
    InveriteRequestGUID: '',
    IsDefault: '',
    Phone: '',
    PlaidAccountID: '',
    'PostalCode/ZipCode': '',
    'Province/State': '',
    Token: '',
    FiservToken: '',
    IntegrationType: '',
    MxAuthorizationCode: '',
    MxProcessorToken: '',
    PlaidProcessorToken: '',
  });

  const {
    response: { AccountType, ClientAccountsEnabled, Country, KYCLightEnabled, ACHWithdrawProcessor },
  } = useClientLogin();
  const [clientAccountBalanceResponse, getClientAccountBalance] = useFetchClientAccountBalance({});
  const availableBalance =
    Number(ClientAccountsEnabled) === 1 ? clientAccountBalanceResponse.AvailableFunds : accountBalance?.AvailableFunds;

  const [currency, setCurrency] = useState('CAD');

  const addMoreInformation = () => {
    setMoreInfoNeeded(true);
  };
  const { permissions } = usePermissions();

  useEffect(() => {
    dataFetchIndicator(loading);
  }, [loading]);

  useEffect(() => {
    if (
      SinglePaymentBankInfoCtx.bankAccount &&
      SinglePaymentBankInfoCtx.bankAccount.AccountHolderName &&
      bankInfo !== SinglePaymentBankInfoCtx.bankAccount
    ) {
      setBankInfo(SinglePaymentBankInfoCtx.bankAccount);
    }
  }, [SinglePaymentBankInfoCtx.bankAccount]);

  useEffect(() => {
    // Initialize timeoutRef variable

    // Function to send the request
    const sendRequest = () => {
      if (clientAccount !== '') {
        getClientAccountBalance({
          ClientAccountID: clientAccount,
          Currency: currency,
        });
      }
    };

    // Set a new timeout to send the request after 100 milliseconds
    const timeoutRef = setTimeout(sendRequest, 100);

    // Cleanup function to clear the timeout on unmount or when dependencies change
    return () => {
      clearTimeout(timeoutRef);
    };
  }, [clientAccount, currency]);

  useEffect(() => {
    if (defaultValues.Currency !== '') {
      setCurrency(defaultValues?.Currency || 'CAD');
    }
  }, [defaultValues.Currency]);

  useEffect(() => {
    if (resetPendingData) {
      reset();
    }
  }, [resetPendingData]);

  return (
    <Formik
      initialValues={defaultValues}
      initialTouched={defaultTouches}
      onSubmit={handlers.handleSubmit}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({
        values,
        touched,
        errors,
        isValid,
        handleChange,
        handleBlur,
        submitForm,
        setValues,
        setTouched,
        handleSubmit,
        setFieldTouched,
        setFieldValue,
        validateField,
      }) => {
        const getGoogleCountries = values.Country === 'CA' ? vp.CANADA_COUNTRY_OPTION : vp.CANADA_US_COUNTRY_OPTION;
        const validCountries = Object.keys(getGoogleCountries || {});
        const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const { name, value } = event.currentTarget;
          setFieldValue(name, value);
          handleChange(event);
        };

        // maybe change to focus out

        useEffect(() => {
          const controller = new AbortController();

          const signal = controller.signal;
          const debounceTimeout = setTimeout(async () => {
            const isValidEmail = Yup.string().email().isValidSync(values.RecipientEmailAddress);
            if (
              values.Contact !== '' &&
              values.Contact !== 'request' &&
              values.PaymentMethod === 'Interac e-Transfer' &&
              values.EtransferConnectionType === 'email' &&
              isValidEmail
            ) {
              try {
                const resp = await fetchAutoDepositStatus(values.RecipientEmailAddress);
                if (!signal.aborted) {
                  setAutoDepositEnabled(resp.AutoDepositEnabled);
                }
              } catch {
                setAutoDepositEnabled(false);
              }
            }
          }, 500);
          if (values.EtransferConnectionType === 'phone') {
            setAutoDepositEnabled(false);
          }
          return () => {
            clearTimeout(debounceTimeout);
            controller.abort();
          };
        }, [values.RecipientEmailAddress, values.Contact, values.PaymentMethod, values.EtransferConnectionType]);

        useEffect(() => {
          if (bankInfo && values.PaymentMethod === 'Bank Account') {
            setValues({
              ...values,
              RecipientName: bankInfo.AccountHolderName,
              Contact: bankInfo.Token,
              Address1: bankInfo.Address,
              City: bankInfo.City,
              Province: bankInfo['Province/State'],
              Country: Country,
              PostalCode: bankInfo['PostalCode/ZipCode'],
              RecipientPhoneNumber: bankInfo.Phone,
              RecipientEmailAddress: bankInfo.EmailAddress,
            });
          }
          if (bankInfo && values.BankingPaymentRail === 'fedwire') {
            setValues({
              ...values,
              Address1: bankInfo.Address,
              City: bankInfo.City,
              Province: bankInfo['Province/State'],
              Country: Country,
              PostalCode: bankInfo['PostalCode/ZipCode'],
              RecipientPhoneNumber: bankInfo.Phone,
              RecipientEmailAddress: bankInfo.EmailAddress,
            });
          }
        }, [bankInfo]);

        useEffect(() => {
          if (values.Contact === 'provide' || values.Contact === 'request') {
            setFieldValue('Address1', '');
            setFieldValue('City', '');
            setFieldValue('Province', '');
            setFieldValue('Country', Country);
            setFieldValue('PostalCode', '');
            setFieldValue('RecipientPhoneNumber', '');
            setFieldValue('RecipientEmailAddress', '');
          }
        }, [values.Contact]);

        useEffect(() => {
          handlers.onSinglePaymentChange(
            values,
            errors,
            touched,
            submitForm,
            transactionDetailRow,
            contactDetailRow,
            addressDetailRow,
            bankDetailRow,
            interacDetailRow,
          );
        }, [values, errors]);

        useEffect(() => {
          if (defaultValues.Contact !== values.Contact || values.PaymentMethod !== defaultValues.PaymentMethod) {
            setValues({ ...values, Contact: '', Amount: 0 });
            setTouched({ ...touched, Contact: false, Amount: false });
          }
        }, [values.PaymentMethod]);

        useEffect(() => {
          if (defaultValues.ContactType !== values.ContactType) {
            setValues({ ...values, FirstName: '', LastName: '', CompanyName: '' });
            setTouched({ ...touched, FirstName: false, LastName: false, CompanyName: false });
          }
        }, [values.ContactType]);

        useEffect(() => {
          if (
            defaultValues.Contact !== values.Contact ||
            values.PaymentMethod !== defaultValues.PaymentMethod ||
            values.Currency !== defaultValues.Currency
          ) {
            setValues({ ...values, Contact: '', PaymentMethod: 'Bank Account', Amount: 0 });
            setTouched({ ...touched, Contact: false, Amount: false });
          }
        }, [values.Currency]);

        useEffect(() => {
          initPendingData({
            ...values,
            Amount: values.Amount?.toString() || '0',
            CancellationPeriod: String(values.CancellationPeriod),
          });
        }, []);
        useEffect(() => {
          if (hasNoBankAccount) {
            setFieldTouched('ClientAccount', true);
            validateField('ClientAccount');
          }
        }, [defaultValues, defaultTouches, hasNoBankAccount]);

        useEffect(() => {
          updatePendingData({
            ...values,
            Amount: values.Amount?.toString() || '0',
            CancellationPeriod: String(values.CancellationPeriod),
          });
        }, [values.Amount]);

        useEffect(() => {
          if (availableBalance && availableBalance !== '') {
            handleChange(vp.createSyntheticEvent('Amount', values.Amount));
          }
        }, [availableBalance]);

        useEffect(() => {
          if (values.PaymentMethod === 'Interac e-Transfer') {
            setValues({
              ...values,
              EtransferConnectionType: InteracCtx.interacDetails?.RecipientEmailAddress
                ? 'email'
                : InteracCtx.interacDetails?.RecipientPhoneNumber
                  ? 'phone'
                  : 'email',
              Answer: InteracCtx.interacDetails?.Answer || '',
              ClientReferenceNumber: InteracCtx.interacDetails?.ClientReferenceNumber || '',
              Question: InteracCtx.interacDetails?.Question || '',
              RecipientEmailAddress: InteracCtx.interacDetails?.RecipientEmailAddress || '',
              RecipientName: InteracCtx.interacDetails?.RecipientName || '',
              RecipientPhoneNumber: InteracCtx.interacDetails?.RecipientPhoneNumber || '',
            });

            setTouched({
              ...touched,
              Answer: false,
              ClientReferenceNumber: false,
              Question: false,
              RecipientEmailAddress: false,
              RecipientName: false,
              RecipientPhoneNumber: false,
            });
            //fixes issue with schema not updating not an issue on the new form builder
            handleChange(
              vp.createSyntheticEvent('RecipientEmailAddress', InteracCtx.interacDetails?.RecipientEmailAddress || ''),
            );
          }
        }, [InteracCtx.interacDetails]);

        useEffect(() => {
          if (
            (values.PaymentMethod === 'Interac e-Transfer' && values.Contact !== '') ||
            (values.PaymentMethod === 'Interac e-Transfer' && values.ClientAccount !== '')
          ) {
            shouldAddInterac(true);
          } else {
            shouldAddInterac(false);
          }
          if (values.PaymentMethod === 'Bank Account') {
            setFieldValue('AccountConnectionMethod', ['Manual', 'Online'].join('|'));
          }
        }, [values.PaymentMethod, values.Contact, values.ClientAccount]);

        useEffect(() => {
          if (vp.toBool(ClientAccountsEnabled)) {
            if (values.PaymentMethod === 'Bank Account' && !hasNoBankAccount) {
              setFieldValue('ProvideOrRequestPaymentInformation', 'provide');
            }
          }
        }, [values.ClientAccount, values.ProvideOrRequestPaymentInformation, hasNoBankAccount]);

        useEffect(() => {
          if (
            values.SendNotification !== defaultValues.SendNotification ||
            values.SendReminder !== defaultValues.SendReminder ||
            values.NotificationMethod !== defaultValues.NotificationMethod ||
            values.ReminderFrequency !== defaultValues.ReminderFrequency
          ) {
            setValues({
              ...values,
              SendNotification: defaultValues.SendNotification,
              SendReminder: defaultValues.SendReminder,
              NotificationMethod: defaultValues.NotificationMethod,
              ReminderFrequency: defaultValues.ReminderFrequency,
            });
          }
        }, [
          defaultValues.SendNotification,
          defaultValues.SendReminder,
          defaultValues.NotificationMethod,
          defaultValues.ReminderFrequency,
        ]);

        const TransactionDetailProps: vp.TableProps = {
          name: 'payment-fields',
          variant: 'pair',
          title: 'Transaction Details',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: transactionDetailRow
            .filter((x) => x.visible(values))
            .map((mapItem) => {
              const subElement = mapItem.getSubElement ? mapItem.getSubElement(values) : undefined;
              return {
                Field: (
                  <Box jc="fs" flx="col">
                    <Box ai="center">
                      {mapItem.label} {getRequiredIcon(mapItem.require(values))}
                      {mapItem.id === 'TakeFromAccountOn' && (
                        <Tooltip
                          type="hover"
                          information="The payment/collection transaction will be initiated immediately after the user has connected their bank account."
                          position="right"
                          hasArrow={false}
                          icon="question"
                        />
                      )}
                    </Box>
                  </Box>
                ),
                Input: (
                  <Box flx="col">
                    {mapItem.type === 'input' ? (
                      <TextField
                        preset={mapItem.preset}
                        key={mapItem.id}
                        name={mapItem.id}
                        value={String(values[mapItem.id])}
                        variant="standard"
                        placeholder={mapItem.placeholder}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        maxLength={mapItem.maxLength}
                        error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                        disabled={!!isUnderReview}
                      />
                    ) : mapItem.type === 'singleDate' ? (
                      <div className={styles.datePickerContainer}>
                        <DatepickerSimple
                          name={mapItem.id}
                          value={String(values[mapItem.id])}
                          error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            if (e.target && e.target.value === 'undefined') {
                              e.target.value = String(values[mapItem.id]);
                            }
                            handleChange(e);
                          }}
                          isDisabled={!!isUnderReview}
                        />
                      </div>
                    ) : mapItem.id === 'ClientAccount' ? (
                      <Select
                        options={
                          mapItem.getSelectOptions ? mapItem.getSelectOptions(values) : mapItem.customOptions || []
                        }
                        renderOption={mapItem.renderOption}
                        variant="standard"
                        lockedValues={lockedFeatures.LockedSelectOptions}
                        lockedCallback={(e: string) => updateIsLocked(true, e)}
                        key={mapItem.id}
                        name={mapItem.id}
                        value={String(values[mapItem.id])}
                        placeholder={mapItem.placeholder}
                        noOrdering
                        onBlur={handleBlur}
                        onChange={(e) => {
                          handleChange(e);

                          if (mapItem.onChangeCallBack) {
                            mapItem.onChangeCallBack(e, values, setFieldValue, setFieldTouched);
                          }

                          getClientAccount(e.target.value);
                        }}
                        error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                        disabled={!!isUnderReview}
                        disableFiltering={mapItem.disableFiltering ? mapItem.disableFiltering(values) : undefined}
                        labelOverride={mapItem.SelectLabel ? mapItem.SelectLabel(values) : undefined}
                        searchCallback={
                          mapItem.disableFiltering
                            ? mapItem.disableFiltering(values)
                              ? mapItem.searchCallback
                              : undefined
                            : undefined
                        }
                      />
                    ) : (
                      <Select
                        options={
                          mapItem.getSelectOptions ? mapItem.getSelectOptions(values) : mapItem.customOptions || []
                        }
                        renderDropdownHeader={({ close }) =>
                          mapItem.renderDropdownHeader &&
                          mapItem.renderDropdownHeader((value: string) => {
                            if (mapItem.id !== 'Contact') return;

                            setAddingNewContact(value === 'provide');
                            setRequestingDetails(value === 'request');
                            setFieldValue('Contact', value);

                            close();
                          }, permissions)
                        }
                        variant="standard"
                        lockedValues={lockedFeatures.LockedSelectOptions}
                        lockedCallback={(e: string) => updateIsLocked(true, e)}
                        key={mapItem.id}
                        name={mapItem.id}
                        value={String(values[mapItem.id])}
                        placeholder={mapItem.placeholder}
                        noOrdering
                        onBlur={(e) => {
                          handleBlur(e);

                          if (mapItem.onBlur) {
                            mapItem.onBlur();
                          }
                        }}
                        customAppendNoResultErrorMessage={mapItem.customAppendNoResultErrorMessage}
                        labelOverride={mapItem.SelectLabel ? mapItem.SelectLabel(values) : undefined}
                        disableFiltering={mapItem.disableFiltering ? mapItem.disableFiltering(values) : undefined}
                        searchCallback={
                          mapItem.disableFiltering
                            ? mapItem.disableFiltering(values)
                              ? mapItem.searchCallback
                              : undefined
                            : undefined
                        }
                        onChange={(e) => {
                          if (mapItem.id === 'Contact' && !['provide', 'request'].includes(e.target.value)) {
                            setAddingNewContact(false);
                            setRequestingDetails(false);
                          }

                          if (mapItem.id === 'Currency') {
                            handlers.handleBalanceForCurrency(e.target.value);
                          }

                          if (mapItem.onChangeCallBack) {
                            mapItem.onChangeCallBack(e, values, setFieldValue, setFieldTouched);
                          }

                          handleChange(e);
                          mapItem.id === 'Currency' ? setNewCurrencyInfo(e.target.value) : handleChange(e);
                        }}
                        error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                        disabled={!!isUnderReview}
                      />
                    )}
                    {subElement}
                  </Box>
                ),
              };
            }),
        };

        const ContactDetailProps: vp.TableProps = {
          name: 'new-contact-fields',
          variant: 'pair',
          title: values.Contact === 'request' ? 'Recipient Details' : 'Contact Details',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: contactDetailRow
            .filter((x) => x.visible(values))
            .map((mapItem) => ({
              Field: (
                <Box jc="fs" flx="col">
                  <Box ai="center">
                    {mapItem.label} {getRequiredIcon(mapItem.require(values))}
                  </Box>
                </Box>
              ),
              Input: (
                <TextField
                  preset={mapItem.preset}
                  key={mapItem.id}
                  name={mapItem.id}
                  value={String(values[mapItem.id])}
                  variant="standard"
                  placeholder={mapItem.placeholder}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  maxLength={mapItem.maxLength}
                  error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                  disabled={!!isUnderReview}
                />
              ),
            })),
        };

        const AddressDetailProps: vp.TableProps = {
          name: 'new-contact-fields',
          variant: 'pair',
          title: 'Account Address',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: addressDetailRow
            .filter((x) => x.visible(values))
            .map((mapItem) => ({
              Field: (
                <Box jc="fs" flx="col">
                  <Box ai="center">
                    {mapItem.label} {getRequiredIcon(mapItem.require(values))}
                  </Box>
                </Box>
              ),
              Input:
                mapItem.type === 'input' && mapItem.id === 'Address1' ? (
                  <GoogleAddressField
                    variant="standard"
                    ISOCountry={validCountries}
                    preset={mapItem.preset}
                    placeholder={mapItem.placeholder}
                    name={mapItem.id}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={String(values[mapItem.id])}
                    error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                    onGoogleAddressSelect={(addressComponents: vp.AddressComponents) => {
                      handleFieldChange(vp.createSyntheticEvent('Address1', addressComponents.address));
                      handleFieldChange(vp.createSyntheticEvent('City', addressComponents.city));
                      handleFieldChange(vp.createSyntheticEvent('Province', addressComponents.province));
                      handleFieldChange(vp.createSyntheticEvent('PostalCode', addressComponents.postalcode));
                    }}
                    disabled={!!isUnderReview}
                  />
                ) : mapItem.type === 'input' ? (
                  <TextField
                    preset={mapItem.preset}
                    key={mapItem.id}
                    name={mapItem.id}
                    value={String(values[mapItem.id])}
                    variant="standard"
                    placeholder={mapItem.placeholder}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    maxLength={mapItem.maxLength}
                    error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                    disabled={!!isUnderReview}
                  />
                ) : mapItem.type === 'singleDate' ? (
                  <div className={styles.datePickerContainer}>
                    <DatepickerSimple
                      name={mapItem.id}
                      value={String(values[mapItem.id])}
                      error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        if (e.target && e.target.value === 'undefined') {
                          e.target.value = String(values[mapItem.id]);
                        }
                        handleChange(e);
                      }}
                      isDisabled={!!isUnderReview}
                    />
                  </div>
                ) : (
                  <Select
                    variant="standard"
                    options={mapItem.getSelectOptions ? mapItem.getSelectOptions(values) : {}}
                    lockedValues={lockedFeatures.LockedSelectOptions}
                    lockedCallback={(e: string) => updateIsLocked(true, e)}
                    key={mapItem.id}
                    name={mapItem.id}
                    value={String(values[mapItem.id])}
                    placeholder={mapItem.placeholder}
                    noOrdering
                    disabled={!!isUnderReview}
                    onBlur={handleBlur}
                    labelOverride={mapItem.SelectLabel ? mapItem.SelectLabel(values) : undefined}
                    disableFiltering={mapItem.disableFiltering ? mapItem.disableFiltering(values) : undefined}
                    searchCallback={
                      mapItem.disableFiltering
                        ? mapItem.disableFiltering(values)
                          ? mapItem.searchCallback
                          : undefined
                        : undefined
                    }
                    onChange={(e) => {
                      if (mapItem.onChangeCallBack) {
                        mapItem.onChangeCallBack(e, values, setFieldValue, setFieldTouched);
                      }
                      handleChange(e);
                    }}
                    error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                  />
                ),
            })),
        };

        const BankDetailProps: vp.TableProps = {
          name: 'new-contact-fields',
          variant: 'pair',
          title: 'Bank Information',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: bankDetailRow
            .filter((x) => x.visible(values))
            .map((mapItem) => ({
              Field: (
                <Box jc="fs" flx="col">
                  <Box ai="center">
                    {mapItem.label} {getRequiredIcon(mapItem.require(values))}
                  </Box>
                </Box>
              ),
              Input:
                mapItem.type === 'input' ? (
                  <TextField
                    preset={mapItem.preset}
                    key={mapItem.id}
                    name={mapItem.id}
                    value={String(values[mapItem.id])}
                    variant="standard"
                    placeholder={mapItem.placeholder}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    maxLength={mapItem.maxLength}
                    error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                    disabled={!!isUnderReview}
                  />
                ) : (
                  <Select
                    variant="standard"
                    options={mapItem.getSelectOptions ? mapItem.getSelectOptions(values) : {}}
                    lockedValues={lockedFeatures.LockedSelectOptions}
                    lockedCallback={(e: string) => updateIsLocked(true, e)}
                    key={mapItem.id}
                    name={mapItem.id}
                    value={String(values[mapItem.id])}
                    placeholder={mapItem.placeholder}
                    onBlur={handleBlur}
                    showValue
                    orderByValues
                    labelOverride={mapItem.SelectLabel ? mapItem.SelectLabel(values) : undefined}
                    disableFiltering={mapItem.disableFiltering ? mapItem.disableFiltering(values) : undefined}
                    searchCallback={
                      mapItem.disableFiltering
                        ? mapItem.disableFiltering(values)
                          ? mapItem.searchCallback
                          : undefined
                        : undefined
                    }
                    onChange={(e) => {
                      if (mapItem.onChangeCallBack) {
                        mapItem.onChangeCallBack(e, values, setFieldValue, setFieldTouched);
                      }
                      handleChange(e);
                    }}
                    error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                    disabled={!!isUnderReview}
                  />
                ),
            })),
        };

        const InteracDetail: vp.TableProps = {
          name: 'new-contact-fields',
          variant: 'pair',
          title: 'Interac Details',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: interacDetailRow
            .filter((x) => x.visible(values))
            .map((mapItem) => ({
              Field: (
                <Box jc="fs" flx="col">
                  <Box ai="center">
                    {mapItem.label} {getRequiredIcon(mapItem.require(values))}
                  </Box>
                </Box>
              ),
              Input:
                mapItem.type === 'input' ? (
                  <TextField
                    preset={mapItem.preset}
                    key={mapItem.id}
                    name={mapItem.id}
                    disabled={!!isUnderReview}
                    value={String(values[mapItem.id])}
                    variant="standard"
                    placeholder={mapItem.placeholder}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    maxLength={mapItem.maxLength}
                    error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                  />
                ) : (
                  <Select
                    variant="standard"
                    options={mapItem.getSelectOptions ? mapItem.getSelectOptions(values) : {}}
                    lockedValues={lockedFeatures.LockedSelectOptions}
                    lockedCallback={(e: string) => updateIsLocked(true, e)}
                    key={mapItem.id}
                    name={mapItem.id}
                    disabled={!!isUnderReview}
                    value={String(values[mapItem.id])}
                    placeholder={mapItem.placeholder}
                    onBlur={handleBlur}
                    orderByValues
                    labelOverride={mapItem.SelectLabel ? mapItem.SelectLabel(values) : undefined}
                    disableFiltering={mapItem.disableFiltering ? mapItem.disableFiltering(values) : undefined}
                    searchCallback={
                      mapItem.disableFiltering
                        ? mapItem.disableFiltering(values)
                          ? mapItem.searchCallback
                          : undefined
                        : undefined
                    }
                    onChange={(e) => {
                      if (mapItem.onChangeCallBack) {
                        mapItem.onChangeCallBack(e, values, setFieldValue, setFieldTouched);
                      }
                      handleChange(e);
                    }}
                    error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                  />
                ),
            })),
        };

        const RequestDetail: vp.TableProps = {
          name: 'new-contact-fields',
          variant: 'pair',
          title: 'Request Details',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: requestDetailRow
            .filter((x) => x.visible(values))
            .map((mapItem, index) => ({
              Field: (
                <Box jc="fs" flx="col">
                  <Box ai="center">
                    {mapItem.label}
                    {getRequiredIcon(mapItem.require(values))}
                    {mapItem.tooltipMessage && (
                      <>
                        <span>&nbsp;</span>
                        <Tooltip
                          type="hover"
                          information={mapItem.tooltipMessage}
                          position="right"
                          hasArrow={false}
                          icon="question"
                        />
                      </>
                    )}
                  </Box>
                </Box>
              ),
              Input:
                mapItem.type === 'input' ? (
                  <TextField
                    preset={mapItem.preset}
                    key={mapItem.id}
                    name={mapItem.id}
                    disabled={!!isUnderReview || mapItem.disabled(values)}
                    value={String(values[mapItem.id])}
                    variant="standard"
                    placeholder={mapItem.placeholder}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    maxLength={mapItem.maxLength}
                    error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                  />
                ) : mapItem.type === 'toggle' ? (
                  <Box jc="fs">
                    <Toggle
                      name={mapItem.id}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled={!!isUnderReview}
                      value={Boolean(values[mapItem.id])}
                    />
                  </Box>
                ) : mapItem.type === 'radio' ? (
                  <Radio
                    name={mapItem.id}
                    value={String(values[mapItem.id])}
                    varient="inline"
                    readOnly={!!isUnderReview || mapItem.disabled(values)}
                    options={mapItem.getRadioOptions ? mapItem.getRadioOptions(values) : {}}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setCheckedValue={(value) => setFieldValue(`NotificationMethod`, value)}
                  />
                ) : (
                  <div className={styles.seletorContainer}>
                    <MultiSelector
                      name={mapItem.id}
                      value={String(values[mapItem.id])}
                      items={mapItem.getMultiSelectOptions ? mapItem.getMultiSelectOptions(values) : []}
                      identifier={`${mapItem.id}-${String(index)}`}
                      labelPosition="right"
                      labelSize="16px"
                      indicatorMinHeight="48px"
                      borderColor="#C4C4C4"
                      setFieldValue={setFieldValue}
                      nosort
                      onBlur={() => {}}
                      disabled={!!isUnderReview}
                    />
                  </div>
                ),
            })),
        };

        return (
          <>
            <Table {...TransactionDetailProps} />
            {!moreInfoNeeded && (
              <div style={{ paddingRight: '1em' }}>
                <u>
                  <Button
                    noShadow
                    id="add-more-information-button"
                    label="+ Add More Information"
                    color="secondary"
                    onClick={() => {
                      addMoreInformation();
                    }}
                  />
                </u>
              </div>
            )}
            {((((addingNewContact && !addInterac) || requestingDetails) && !vp.toBool(ClientAccountsEnabled)) ||
              (values.PaymentMethod === 'Bank Account' &&
                Country === 'US' &&
                (!bankInfo.Phone || !bankInfo.EmailAddress) &&
                values.Contact !== '') ||
              (values.PaymentMethod === 'Bank Account' &&
                Country === 'US' &&
                values.ClientAccount !== '' &&
                !values.ClientAccountPhoneNumber)) && (
              <>
                <Table {...ContactDetailProps} />
              </>
            )}
            {((addingNewContact && !addInterac) ||
              (Country === 'US' &&
                values.PaymentMethod === 'Bank Account' &&
                values.Contact !== '' &&
                !requestingDetails)) && (
              <>
                <Table {...AddressDetailProps} />
              </>
            )}
            {values.ProvideOrRequestPaymentInformation === 'provide' && addInterac && (
              <>
                <Table {...InteracDetail} />
              </>
            )}
            {values.ProvideOrRequestPaymentInformation === 'provide' && addInterac && autoDepositEnabled && (
              <MessageBox>
                <Box flx="row" ai="center" gapSize="16px">
                  <img src={GppGoodIcon} alt="good icon" />

                  <div>
                    This contact has <span style={{ fontWeight: 600 }}>auto deposit</span> enabled, so no security
                    question is needed. Please note that this transfer cannot be cancelled.
                  </div>
                </Box>
              </MessageBox>
            )}

            {values.Contact === 'request' && <Table {...RequestDetail} />}

            {addingNewContact &&
              getAccountCreationValue(values.PaymentMethod) === 'bank' &&
              !addInterac &&
              values.ProvideOrRequestPaymentInformation === 'provide' && (
                <>
                  <Table {...BankDetailProps} />
                </>
              )}
            <SinglePaymentDialogueContext.Provider value={SinglePaymentContext}>
              <Box jc="sb">
                <Box jc="fs">
                  {(values.ProvideOrRequestPaymentInformation === 'request' || values.Contact === 'request') && (
                    <Button
                      id="Fund-submit"
                      color="secondary"
                      type="submit"
                      label="Edit eLinx Settings"
                      onClick={() => {
                        !lockedFeatures.PayLinkEnabled || !permissions.ViewElinxSettings
                          ? updateIsLocked(true, 'Elinx')
                          : setIsVisible(true);
                      }}
                    />
                  )}
                </Box>
                <Box flx="col" gap="common">
                  {(values.ProvideOrRequestPaymentInformation === 'request' || values.Contact === 'request') && (
                    <Box jc="fe">
                      <Button
                        id="See-Preview"
                        color="secondary"
                        type="button"
                        label="See Preview"
                        onClick={() => setPreviewVisible(true)}
                      />
                    </Box>
                  )}
                  <form onSubmit={handleSubmit}>
                    <Box mt="md" jc="fe" gap="sm">
                      {isUnderReview ? (
                        <Button id="Fund-back" color="secondary" label="Back" onClick={() => setIsUnderReview(false)} />
                      ) : null}
                      <Button
                        id="Fund-submit"
                        isDisabled={!isValid}
                        type="submit"
                        label={isUnderReview ? 'Submit' : 'Review'}
                      />
                    </Box>
                  </form>
                </Box>
              </Box>
              <Prompt when={hasPendingData} message="You have unsaved account data that will be discarded." />
              <SinglePaymentDialogue handleDialogue={handlers.handleSinglePaymentDialogue} />
              <Modal isVisible={previewVisible} enableBgClick={false} zIndex={10}>
                <div className={styles.container}>
                  <Box flx="col" ai="center">
                    <div className={styles.TitleContainer}>
                      <h1>eLinx Preview</h1>
                    </div>
                    <div className={styles.DescriptionContainer}>
                      <p>This is a preview of your eLinx Connect Request. If any adjustments</p>
                      <p>need to be made to eLinx itself, go to Modify eLinx Settings.</p>
                    </div>
                    <div>
                      <img src={ElinxImage} alt="elinx-preview" />
                    </div>
                    <div className={styles.ButtonBar}>
                      <Button
                        id="ManageBranding-Settings"
                        label="Manage Branding Settings"
                        color="secondary"
                        type="button"
                        onClick={() => {
                          history.push('/settings/brand-settings');
                        }}
                      />
                      <Button
                        id="AddLabel-Accept"
                        label="Confirm and Close"
                        color="primary"
                        type="button"
                        onClick={() => {
                          setPreviewVisible(false);
                        }}
                      />
                    </div>
                  </Box>
                </div>
              </Modal>
            </SinglePaymentDialogueContext.Provider>
          </>
        );
      }}
    </Formik>
  );

  function getAccountCreationValue(initialValue: string): string {
    if (initialValue === 'Bank Account') {
      return 'bank';
    }
    if (initialValue === 'vopayinstant') {
      return 'bank';
    }
    if (initialValue === 'Interac e-Transfer') {
      return 'e-transfer';
    }
    if (initialValue === 'Credit Card') {
      return 'creditCard';
    }
    return initialValue;
  }

  function createdValidationSchema() {
    return Yup.object().shape({
      Currency: Yup.string().required('This value is required.'),
      PaymentMethod: Yup.string().required('This value is required.'),
      CancellationPeriod: Yup.string().test(
        'cancellation-period-validation',
        'This value cannot exceed 30 days',
        (value) => {
          const periodValidation = Number(value) > 30;
          return !periodValidation;
        },
      ),
      BankingPaymentRail: Yup.string().when(['PaymentMethod'], {
        is: (PaymentMethod: string) => PaymentMethod === 'Bank Account',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      Contact:
        Number(ClientAccountsEnabled) === 1
          ? Yup.string().notRequired()
          : Yup.string().required('This value is required.'),
      ClientAccount: Yup.string()
        .when([], {
          is: () => Number(ClientAccountsEnabled) === 0,
          then: Yup.string().notRequired(),
          otherwise: Yup.string().required('This value is required.'),
        })
        .when('PaymentMethod', {
          is: 'Bank Account',
          then: Yup.string().test(
            'has-bank-validation',
            `Please go to <a href="/client-accounts/${
              defaultValues.ClientAccount || ''
            }">client account details</a> to set a default payment method.`,
            () => !vp.toBool(ClientAccountsEnabled) || !hasNoBankAccount,
          ),
        })
        .when('PaymentMethod', {
          is: 'Venmo',
          then: Yup.string().test(
            'has-bank-validation',
            `Please go to <a href="/client-accounts/${
              defaultValues.ClientAccount || ''
            }">client account details</a> to set a default payment method.`,
            () => !vp.toBool(ClientAccountsEnabled) || hasVenmoAccount,
          ),
        })
        .when('PaymentMethod', {
          is: 'PayPal',
          then: Yup.string().test(
            'has-bank-validation',
            `Please go to <a href="/client-accounts/${
              defaultValues.ClientAccount || ''
            }">client account details</a> to set a default payment method.`,
            () => !vp.toBool(ClientAccountsEnabled) || hasPayPalAccount,
          ),
        })
        .when('BankingPaymentRail', {
          is: 'vopayinstant',
          then: Yup.string().test(
            'has-bank-validation',
            `Please go to <a href="/client-accounts/${
              defaultValues.ClientAccount || ''
            }">client account details</a> to set a default payment method.`,
            () => !vp.toBool(ClientAccountsEnabled) || !hasNoBankAccount,
          ),
        }),
      Amount: Yup.string()
        .test('amount-validation', 'This value should be greater than or equal to $0.01.', (value) => {
          const amountValidation = Number(value) <= 0.0;
          return !amountValidation;
        })
        .test('amount-validation', `Insufficient funds. Maximum allowed is: ${String(availableBalance)}`, (value) => {
          const availableFundsValidation = Number(value) > Number(availableBalance) && AccountType !== 'PaaS';
          return !availableFundsValidation;
        })
        .required('This value is required.'),
      ScheduleType: Yup.string().when(['PaymentMethod', 'BankingPaymentRail', 'Currency'], {
        is: (PaymentMethod: string, BankingPaymentRail: vp.BankingPaymentRail, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          (BankingPaymentRail === 'eft' || BankingPaymentRail === 'ach') &&
          Country === 'CA' &&
          Currency === 'CAD',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      TakeFromAccountOn: Yup.string().when(['PaymentMethod', 'BankingPaymentRail', 'ScheduleType', 'Currency'], {
        is: (
          PaymentMethod: string,
          BankingPaymentRail: vp.BankingPaymentRail,
          ScheduleType: string,
          Currency: string,
        ) =>
          PaymentMethod === 'Bank Account' &&
          (BankingPaymentRail === 'eft' || BankingPaymentRail === 'ach') &&
          ScheduleType === 'single' &&
          Country === 'CA' &&
          Currency === 'CAD',
        then: Yup.string()
          .test(
            'start-date-validation',
            `Invalid date please choose a date on or after ${moment(now).format('YYYY-MM-DD')}`,
            (value: any) => {
              const dateValidation = value !== 'underfined' && moment(Number(value)).format('YYYY-MM-DD') < now;
              return !dateValidation;
            },
          )
          .required('This value is required.'),
        otherwise: Yup.string(),
      }),
      StartDate: Yup.string().when(['PaymentMethod', 'BankingPaymentRail', 'ScheduleType', 'Currency'], {
        is: (
          PaymentMethod: string,
          BankingPaymentRail: vp.BankingPaymentRail,
          ScheduleType: string,
          Currency: string,
        ) =>
          PaymentMethod === 'Bank Account' &&
          (BankingPaymentRail === 'eft' || BankingPaymentRail === 'ach') &&
          ScheduleType === 'recurring' &&
          Country === 'CA' &&
          Currency === 'CAD',
        then: Yup.string()
          .test(
            'start-date-validation',
            `Invalid date. Please choose a date on or after ${moment(now).add(1, 'days').format('YYYY-MM-DD')}`,
            (value: string | undefined) => {
              const dateValidation =
                value !== 'underfined' &&
                moment(Number(value)).format('YYYY-MM-DD') < moment(now).add(1, 'days').format('YYYY-MM-DD');
              return !dateValidation;
            },
          )
          .required('This value is required.'),
        otherwise: Yup.string(),
      }),
      Frequency: Yup.string().when(['PaymentMethod', 'BankingPaymentRail', 'ScheduleType', 'Currency'], {
        is: (
          PaymentMethod: string,
          BankingPaymentRail: vp.BankingPaymentRail,
          ScheduleType: string,
          Currency: string,
        ) =>
          PaymentMethod === 'Bank Account' &&
          (BankingPaymentRail === 'eft' || BankingPaymentRail === 'ach') &&
          ScheduleType === 'recurring' &&
          Country === 'CA' &&
          Currency === 'CAD',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      SemiMonthlyDates: Yup.string().when(['PaymentMethod', 'BankingPaymentRail', 'ScheduleType', 'Frequency'], {
        is: (
          PaymentMethod: string,
          BankingPaymentRail: vp.BankingPaymentRail,
          ScheduleType: string,
          Frequency: string,
        ) =>
          PaymentMethod === 'Bank Account' &&
          (BankingPaymentRail === 'eft' || BankingPaymentRail === 'ach') &&
          ScheduleType === 'recurring' &&
          Frequency === 'semi-monthly' &&
          Country === 'CA',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      Ending: Yup.string().when(['PaymentMethod', 'BankingPaymentRail', 'ScheduleType', 'Currency'], {
        is: (
          PaymentMethod: string,
          BankingPaymentRail: vp.BankingPaymentRail,
          ScheduleType: string,
          Currency: string,
        ) =>
          PaymentMethod === 'Bank Account' &&
          (BankingPaymentRail === 'eft' || BankingPaymentRail === 'ach') &&
          ScheduleType === 'recurring' &&
          Country === 'CA' &&
          Currency === 'CAD',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      EndDate: Yup.string().when(['PaymentMethod', 'BankingPaymentRail', 'ScheduleType', 'Ending', 'Currency'], {
        is: (
          PaymentMethod: string,
          BankingPaymentRail: vp.BankingPaymentRail,
          ScheduleType: string,
          Ending: string,
          Currency: string,
        ) =>
          PaymentMethod === 'Bank Account' &&
          (BankingPaymentRail === 'eft' || BankingPaymentRail === 'ach') &&
          ScheduleType === 'recurring' &&
          Ending === 'End Date' &&
          Country === 'CA' &&
          Currency === 'CAD',
        then: Yup.string()
          .test(
            'end-date-validation',
            'Invalid date. Please choose a date on or after start date',
            (value: string | undefined, StartDate: StartDateType) => {
              const endDate = new Date(moment(Number(value)).format('YYYY-MM-DD'));
              const startDate = new Date(moment(Number(StartDate.parent.StartDate)).format('YYYY-MM-DD'));
              const dateValidation = endDate === startDate || endDate < startDate;
              return !dateValidation;
            },
          )
          .required('This value is required.'),
        otherwise: Yup.string(),
      }),
      EndingAfterPayments: Yup.string().when(['PaymentMethod', 'BankingPaymentRail', 'ScheduleType', 'Ending'], {
        is: (PaymentMethod: string, BankingPaymentRail: vp.BankingPaymentRail, ScheduleType: string, Ending: string) =>
          PaymentMethod === 'Bank Account' &&
          (BankingPaymentRail === 'eft' || BankingPaymentRail === 'ach') &&
          ScheduleType === 'recurring' &&
          Ending === 'Payments',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      Description: Yup.string().when(
        ['PaymentMethod', 'BankingPaymentRail', 'ScheduleType', 'Currency', 'TakeFromAccountOn'],
        {
          is: (
            PaymentMethod: string,
            BankingPaymentRail: vp.BankingPaymentRail,
            ScheduleType: string,
            Currency: string,
            TakeFromAccountOn: string,
          ) =>
            PaymentMethod === 'Bank Account' &&
            (BankingPaymentRail === 'eft' || BankingPaymentRail === 'ach') &&
            (ScheduleType === 'recurring' ||
              (TakeFromAccountOn && !vp.IsTodayDate(moment(Number(TakeFromAccountOn)).format('YYYY-MM-DD')))) &&
            Country === 'CA' &&
            Currency === 'CAD',
          then: Yup.string().required('This value is required.'),
          otherwise: Yup.string(),
        },
      ),
      TransactionLabel: Yup.string().max(30, 'The value supplied exceeds the maximum length (max. 30)'),
      Notes: Yup.string().when(['ScheduleType'], {
        is: (ScheduleType: string) => ScheduleType === 'recurring',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      FirstName: Yup.string().when(['PaymentMethod', 'BankingPaymentRail', 'Contact', 'ContactType'], {
        is: (PaymentMethod: string, BankingPaymentRail: vp.BankingPaymentRail, Contact: string, ContactType: string) =>
          (PaymentMethod === 'Bank Account' &&
            (BankingPaymentRail === 'eft' || BankingPaymentRail === 'vopayinstant') &&
            (Contact === 'provide' || Contact === 'request') &&
            ContactType === 'Individual') ||
          (PaymentMethod === 'Interac e-Transfer' && Contact === 'request' && ContactType === 'Individual'),
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      LastName: Yup.string().when(['PaymentMethod', 'BankingPaymentRail', 'Contact', 'ContactType'], {
        is: (PaymentMethod: string, BankingPaymentRail: vp.BankingPaymentRail, Contact: string, ContactType: string) =>
          (PaymentMethod === 'Bank Account' &&
            (BankingPaymentRail === 'eft' || BankingPaymentRail === 'vopayinstant') &&
            (Contact === 'provide' || Contact === 'request') &&
            ContactType === 'Individual') ||
          (PaymentMethod === 'Interac e-Transfer' && Contact === 'request' && ContactType === 'Individual'),
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      CompanyName: Yup.string().when(['BankingPaymentRail', 'Contact', 'ContactType'], {
        is: (PaymentMethod: string, BankingPaymentRail: vp.BankingPaymentRail, Contact: string, ContactType: string) =>
          (PaymentMethod === 'Bank Account' &&
            (BankingPaymentRail === 'eft' || BankingPaymentRail === 'vopayinstant') &&
            (Contact === 'provide' || Contact === 'request') &&
            ContactType === 'Business') ||
          (PaymentMethod === 'Interac e-Transfer' && Contact === 'request' && ContactType === 'Business'),
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      RecipientEmailAddress: Yup.string().when(
        ['Contact', 'PaymentMethod', 'EtransferConnectionType', 'NotificationMethod'],
        {
          is: (Contact: string, PaymentMethod: string, EtransferConnectionType: string, NotificationMethod: string) =>
            (Contact === 'request' && NotificationMethod === 'sms') ||
            (Contact !== 'request' &&
              !vp.toBool(ClientAccountsEnabled) &&
              Country === 'US' &&
              PaymentMethod === 'Bank Account') ||
            (PaymentMethod === 'Interac e-Transfer' && EtransferConnectionType === 'email'),
          then: Yup.string()
            .required('Please enter a valid email address.')
            .email('Please enter a valid Email Address'),
          otherwise: Yup.string(),
        },
      ),
      ReceiverPhoneNumber: Yup.string().when(['Contact', 'NotificationMethod'], {
        is: (Contact: string, NotificationMethod: string) => Contact === 'request' && NotificationMethod === 'sms',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      RecipientPhoneNumber: Yup.string().when(['Contact', 'PaymentMethod', 'EtransferConnectionType'], {
        is: (Contact: string, PaymentMethod: string, EtransferConnectionType: string) =>
          (Contact !== 'request' &&
            !vp.toBool(ClientAccountsEnabled) &&
            Country === 'US' &&
            PaymentMethod === 'Bank Account') ||
          (PaymentMethod === 'Interac e-Transfer' && Contact !== 'request' && EtransferConnectionType === 'phone'),

        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      IdentificationNumber: Yup.string().when(['BankingPaymentRail', 'Contact', 'PaymentMethod'], {
        is: (BankingPaymentRail: vp.BankingPaymentRail, Contact: string, PaymentMethod: string) =>
          PaymentMethod === 'Bank Account' &&
          BankingPaymentRail === 'ach' &&
          (Contact !== 'new' || Number(ClientAccountsEnabled) === 1) &&
          !vp.toBool(KYCLightEnabled) &&
          ACHWithdrawProcessor === 'fiserv',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      Address1: getAddressValidation(),
      City: getAddressValidation(),
      Province: getAddressValidation(),
      Country: getAddressValidation(),
      PostalCode: getAddressValidation(),
      FinancialInstitutionNumber: Yup.string().when(['PaymentMethod', 'Contact'], {
        is: (PaymentMethod: string, Contact: string) =>
          PaymentMethod === 'Bank Account' && Contact === 'provide' && Country === 'CA',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      BranchTransitNumber: Yup.string().when(['PaymentMethod', 'Contact'], {
        is: (PaymentMethod: string, Contact: string) =>
          PaymentMethod === 'Bank Account' && Contact === 'provide' && Country === 'CA',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      ABARoutingNumber: Yup.string().when(['PaymentMethod', 'Contact'], {
        is: (PaymentMethod: string, Contact: string) =>
          PaymentMethod === 'Bank Account' && Contact === 'provide' && Country === 'US',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      AccountNumber: Yup.string().when(['PaymentMethod', 'Contact'], {
        is: (PaymentMethod: string, Contact: string) => PaymentMethod === 'Bank Account' && Contact === 'provide',
        then: Yup.string().required('This value is required.').min(6, 'Account Number must be at least 6 digits'),
        otherwise: Yup.string(),
      }),
      SenderName: Yup.string().when(['PaymentMethod', 'Contact'], {
        is: (PaymentMethod: string, Contact: string) => PaymentMethod === 'Bank Account' && Contact === 'request',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      RecipientName: Yup.string().when(['PaymentMethod', 'BankingPaymentRail', 'Contact'], {
        is: (PaymentMethod: string, BankingPaymentRail: vp.BankingPaymentRail, Contact: string) =>
          (PaymentMethod === 'Interac e-Transfer' && Contact !== 'request') || BankingPaymentRail === 'rtp',
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      Question: Yup.string().when(['PaymentMethod'], {
        is: (PaymentMethod: string) => PaymentMethod === 'Interac e-Transfer' && !autoDepositEnabled,
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
      Answer: Yup.string().when(['PaymentMethod'], {
        is: (PaymentMethod: string) => PaymentMethod === 'Interac e-Transfer' && !autoDepositEnabled,
        then: Yup.string().required('This value is required.'),
        otherwise: Yup.string(),
      }),
    });
  }

  function getAddressValidation() {
    return Yup.string().when(['PaymentMethod', 'Contact'], {
      is: (PaymentMethod: string, Contact: string) =>
        ((PaymentMethod === 'Bank Account' || PaymentMethod === 'VoPay Instant') && Contact === 'provide') ||
        (PaymentMethod === 'Bank Account' &&
          Country === 'US' &&
          Contact !== 'request' &&
          !vp.toBool(ClientAccountsEnabled)),
      then: Yup.string().required('This value is required.'),
      otherwise: Yup.string(),
    });
  }

  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
};

export default SinglePaymentForm;
